<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import FluentIcon from 'shared/components/FluentIcon/DashboardIcon.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';

const props = defineProps({
  record: { type: Object, required: true },
  isAdministrator: { type: Boolean, default: false },
  isSelected: { type: Boolean, default: false },
  isBusy: { type: Boolean, default: false },
});

const emit = defineEmits([
  'select',
  'edit',
  'duplicate',
  'toggleActive',
  'delete',
]);

const { t } = useI18n();
const isMenuOpen = ref(false);

const prefix = key => t(`PIPELINE.SEQUENCE_DEFINITIONS.${key}`);

const hasWebhookStep = computed(
  () =>
    Array.isArray(props.record.steps) &&
    props.record.steps.some(step => step?.activity_type === 'webhook')
);

// Mirrors PipelineSequenceDefinitionPolicy#update?: a definition containing a
// webhook step can only be managed by an administrator.
const canManage = computed(
  () => props.isAdministrator || !hasWebhookStep.value
);

const triggerLabel = computed(() => {
  const map = {
    manual: 'TRIGGER_MANUAL',
    stage_change: 'TRIGGER_STAGE_CHANGE',
    time_based: 'TRIGGER_TIME_BASED',
    condition_based: 'TRIGGER_CONDITION_BASED',
  };
  // A null trigger_type is legacy data. `set_defaults` normalises new writes,
  // but the screen must not claim "Manual" for something it does not know.
  const key = map[props.record.trigger_type];
  return key ? prefix(key) : prefix('TRIGGER_UNDEFINED');
});

const stepCount = computed(
  () => props.record.step_count ?? props.record.steps?.length ?? 0
);

const durationLabel = computed(() =>
  t('PIPELINE.SEQUENCE_DEFINITIONS.DAYS_SHORT', {
    n: props.record.total_duration_days ?? 0,
  })
);

const runningCount = computed(() => props.record.active_executions_count ?? 0);

const withReason = label => `${label} — ${prefix('ADMIN_REQUIRED_SUFFIX')}`;

// DropdownMenu applies `disabled:pointer-events-none`, so a disabled item can
// never surface a tooltip. The reason therefore lives in the label itself.
const menuItems = computed(() => {
  const manageBlocked = !canManage.value;
  const items = [
    {
      label: manageBlocked
        ? withReason(prefix('BTN_EDIT'))
        : prefix('BTN_EDIT'),
      value: 'edit',
      action: 'edit',
      icon: 'i-lucide-pencil',
      disabled: manageBlocked,
    },
    {
      label: manageBlocked
        ? withReason(prefix('BTN_DUPLICATE'))
        : prefix('BTN_DUPLICATE'),
      value: 'duplicate',
      action: 'duplicate',
      icon: 'i-lucide-copy',
      disabled: manageBlocked,
    },
    {
      label: (() => {
        const base = props.record.active
          ? prefix('BTN_DEACTIVATE')
          : prefix('BTN_ACTIVATE');
        return manageBlocked ? withReason(base) : base;
      })(),
      value: 'toggleActive',
      action: 'toggleActive',
      icon: props.record.active
        ? 'i-lucide-circle-pause'
        : 'i-lucide-circle-play',
      disabled: manageBlocked,
    },
  ];

  // Destroy is administrator-only in the policy, with no webhook exception.
  // Offering it to an agent would only produce a guaranteed 403.
  if (props.isAdministrator) {
    items.push({
      label: prefix('BTN_DELETE'),
      value: 'delete',
      action: 'delete',
      icon: 'i-lucide-trash-2',
    });
  }

  return items;
});

const toggleMenu = (value = !isMenuOpen.value) => {
  isMenuOpen.value = value;
};

// Emitted explicitly rather than by forwarding the action string, so each
// event stays statically traceable from the template.
const handleAction = ({ action }) => {
  toggleMenu(false);
  if (action === 'delete') {
    if (props.isAdministrator) emit('delete');
    return;
  }
  if (!canManage.value) return;
  if (action === 'edit') emit('edit');
  else if (action === 'duplicate') emit('duplicate');
  else if (action === 'toggleActive') emit('toggleActive');
};
</script>

<template>
  <tr
    class="transition-colors cursor-pointer hover:bg-n-alpha-1 focus-within:bg-n-alpha-1"
    :class="{ 'bg-n-alpha-2': isSelected }"
    @click="emit('select')"
  >
    <td class="py-3 ltr:pr-4 rtl:pl-4">
      <div class="flex gap-2 items-center min-w-0">
        <span class="text-sm font-medium truncate text-n-slate-12">
          {{ record.name }}
        </span>
        <FluentIcon
          v-if="hasWebhookStep"
          v-tooltip="prefix('ACTIVITY_TYPE_WEBHOOK')"
          icon="arrow-swap"
          size="14"
          class="flex-shrink-0 text-n-amber-11"
        />
      </div>
      <p
        v-if="record.description"
        class="mt-0.5 text-xs truncate text-n-slate-10"
      >
        {{ record.description }}
      </p>
    </td>

    <td class="py-3 ltr:pr-4 rtl:pl-4">
      <span
        class="inline-flex gap-1.5 items-center px-2 py-0.5 rounded-full text-[11px] font-semibold whitespace-nowrap"
        :class="
          record.active
            ? 'bg-n-teal-3 text-n-teal-11'
            : 'bg-n-alpha-2 text-n-slate-10'
        "
      >
        <span class="w-1.5 h-1.5 rounded-full bg-current" />
        {{
          record.active ? prefix('STATUS_ACTIVE') : prefix('STATUS_INACTIVE')
        }}
      </span>
    </td>

    <td
      class="hidden py-3 text-sm md:table-cell ltr:pr-4 rtl:pl-4 text-n-slate-11 tabular-nums"
    >
      {{ stepCount }}
    </td>

    <td
      class="hidden py-3 text-sm lg:table-cell ltr:pr-4 rtl:pl-4 text-n-slate-11 tabular-nums"
    >
      {{ durationLabel }}
    </td>

    <td
      class="hidden py-3 text-sm lg:table-cell ltr:pr-4 rtl:pl-4 text-n-slate-11 whitespace-nowrap"
    >
      {{ triggerLabel }}
    </td>

    <td
      class="hidden py-3 text-sm lg:table-cell ltr:pr-4 rtl:pl-4 tabular-nums"
      :class="
        runningCount > 0 ? 'text-n-slate-12 font-medium' : 'text-n-slate-10'
      "
    >
      {{ runningCount }}
    </td>

    <td class="py-3 w-11">
      <div
        v-on-clickaway="() => toggleMenu(false)"
        class="flex relative justify-end items-center"
        @click.stop
      >
        <Button
          icon="i-lucide-ellipsis-vertical"
          color="slate"
          size="xs"
          class="rounded-md"
          :aria-label="
            t('PIPELINE.SEQUENCE_DEFINITIONS.ROW_ACTIONS_LABEL', {
              name: record.name,
            })
          "
          :disabled="isBusy"
          @click="toggleMenu()"
        />
        <DropdownMenu
          v-if="isMenuOpen"
          :menu-items="menuItems"
          class="top-full mt-1 ltr:right-0 rtl:left-0"
          @action="handleAction"
        />
      </div>
    </td>
  </tr>
</template>
